import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Breadcrumb } from "react-bootstrap";
import { BREADCRUMB_LABELS } from "../constants";

interface DynamicBreadcrumbProps {
    currentPageLabel: string;
}

const findPathSegmentIndex = (pathSegments: string[], segment: string): number => {
    return pathSegments.indexOf(segment);
};

const extractIdFromPath = (pathSegments: string[], segmentIndex: number): string | null => {
    if (segmentIndex === -1) {
        return null;
    }
    const id = pathSegments[segmentIndex + 1];
    return id && id !== "undefined" ? id : null;
};

const checkZoneContext = (
    zoneIndex: number,
    detailIndices: { room: number; sensor: number; controller: number; group: number; hub: number; auxiliaryHub: number }
): boolean => {
    if (zoneIndex === -1) {
        return false;
    }

    return (
        (detailIndices.room !== -1 && zoneIndex < detailIndices.room) ||
        (detailIndices.sensor !== -1 && zoneIndex < detailIndices.sensor) ||
        (detailIndices.controller !== -1 && zoneIndex < detailIndices.controller) ||
        (detailIndices.group !== -1 && zoneIndex < detailIndices.group) ||
        (detailIndices.hub !== -1 && zoneIndex < detailIndices.hub) ||
        (detailIndices.auxiliaryHub !== -1 && zoneIndex < detailIndices.auxiliaryHub)
    );
};

const addRoomBreadcrumb = (
    breadcrumbItems: Array<{ to: string; label: string; active: boolean }>,
    roomDetailsIndex: number,
    roomIdFromUrl: string | null,
    networkIdFromUrl: string | null,
    isInZoneContext: boolean,
    zoneIdFromUrl: string | null,
    currentPageLabel: string
): void => {
    if (roomDetailsIndex === -1 || !roomIdFromUrl || currentPageLabel === BREADCRUMB_LABELS["room-details"]) {
        return;
    }

    if (!networkIdFromUrl) {
        return;
    }

    const roomPath = isInZoneContext && zoneIdFromUrl
        ? `/network-details/${networkIdFromUrl}/zone/${zoneIdFromUrl}/room-details/${roomIdFromUrl}`
        : `/network-details/${networkIdFromUrl}/room-details/${roomIdFromUrl}`;

    breadcrumbItems.push({
        to: roomPath,
        label: BREADCRUMB_LABELS["room-details"],
        active: false,
    });
};

const addGroupBreadcrumb = (
    breadcrumbItems: Array<{ to: string; label: string; active: boolean }>,
    groupDetailsIndex: number,
    groupIdFromUrl: string | null,
    networkIdFromUrl: string | null,
    currentPageLabel: string
): void => {
    if (
        groupDetailsIndex === -1 ||
        !groupIdFromUrl ||
        !networkIdFromUrl ||
        currentPageLabel === BREADCRUMB_LABELS["group-details"]
    ) {
        return;
    }

    breadcrumbItems.push({
        to: `/network-details/${networkIdFromUrl}/group-details/${groupIdFromUrl}`,
        label: BREADCRUMB_LABELS["group-details"],
        active: false,
    });
};

const addHubBreadcrumb = (
    breadcrumbItems: Array<{ to: string; label: string; active: boolean }>,
    hubDetailsIndex: number,
    hubIdFromUrl: string | null,
    networkIdFromUrl: string | null,
    currentPageLabel: string
): void => {
    if (
        hubDetailsIndex === -1 ||
        !hubIdFromUrl ||
        !networkIdFromUrl ||
        currentPageLabel === BREADCRUMB_LABELS["hub-details"]
    ) {
        return;
    }

    breadcrumbItems.push({
        to: `/network-details/${networkIdFromUrl}/hub-details/${hubIdFromUrl}`,
        label: BREADCRUMB_LABELS["hub-details"],
        active: false,
    });
};

export const DynamicBreadcrumb: React.FC<DynamicBreadcrumbProps> = ({ currentPageLabel }) => {
    const location = useLocation();

    const generateBreadcrumbs = () => {
        const pathSegments = location.pathname.split("/").filter(Boolean);
        const breadcrumbItems: Array<{ to: string; label: string; active: boolean }> = [];

        const networkDetailsIndex = findPathSegmentIndex(pathSegments, "network-details");
        const networkIdFromUrl = extractIdFromPath(pathSegments, networkDetailsIndex);

        const zoneIndex = findPathSegmentIndex(pathSegments, "zone");
        const roomDetailsIndex = findPathSegmentIndex(pathSegments, "room-details");
        const groupDetailsIndex = findPathSegmentIndex(pathSegments, "group-details");
        const hubDetailsIndex = findPathSegmentIndex(pathSegments, "hub-details");
        const sensorDetailsIndex = findPathSegmentIndex(pathSegments, "sensor-details");
        const controllerDetailsIndex = findPathSegmentIndex(pathSegments, "controller-details");
        const auxiliaryHubDetailsIndex = findPathSegmentIndex(pathSegments, "auxiliary-hub-details");

        const isInZoneContext = checkZoneContext(zoneIndex, {
            room: roomDetailsIndex,
            sensor: sensorDetailsIndex,
            controller: controllerDetailsIndex,
            group: groupDetailsIndex,
            hub: hubDetailsIndex,
            auxiliaryHub: auxiliaryHubDetailsIndex,
        });

        const zoneIdFromUrl = extractIdFromPath(pathSegments, zoneIndex);
        const roomIdFromUrl = extractIdFromPath(pathSegments, roomDetailsIndex);
        const groupIdFromUrl = extractIdFromPath(pathSegments, groupDetailsIndex);
        const hubIdFromUrl = extractIdFromPath(pathSegments, hubDetailsIndex);

        if (networkDetailsIndex !== -1) {
            breadcrumbItems.push({
                to: "/networks",
                label: BREADCRUMB_LABELS.networks,
                active: false,
            });
        }

        if (networkIdFromUrl) {
            breadcrumbItems.push({
                to: `/network-details/${networkIdFromUrl}`,
                label: BREADCRUMB_LABELS["network-details"],
                active: false,
            });
        }

        if (isInZoneContext && zoneIdFromUrl && networkIdFromUrl) {
            breadcrumbItems.push({
                to: `/network-details/${networkIdFromUrl}/zone/${zoneIdFromUrl}`,
                label: BREADCRUMB_LABELS.zone,
                active: false,
            });
        }

        addRoomBreadcrumb(
            breadcrumbItems,
            roomDetailsIndex,
            roomIdFromUrl,
            networkIdFromUrl,
            isInZoneContext,
            zoneIdFromUrl,
            currentPageLabel
        );

        addGroupBreadcrumb(breadcrumbItems, groupDetailsIndex, groupIdFromUrl, networkIdFromUrl, currentPageLabel);

        addHubBreadcrumb(breadcrumbItems, hubDetailsIndex, hubIdFromUrl, networkIdFromUrl, currentPageLabel);

        breadcrumbItems.push({
            to: "",
            label: currentPageLabel,
            active: true,
        });

        return breadcrumbItems;
    };

    const breadcrumbItems = generateBreadcrumbs();

    return (
        <Breadcrumb className="w-100">
            {breadcrumbItems.map((item) => (
                <Breadcrumb.Item
                    key={item.to || item.label}
                    linkAs={item.active ? undefined : Link}
                    linkProps={item.active ? undefined : { to: item.to }}
                    active={item.active}
                >
                    {item.label}
                </Breadcrumb.Item>
            ))}
        </Breadcrumb>
    );
};

